import React, { useState } from 'react'
import {
  IoArrowBack,
  IoAddOutline,
  IoTrashOutline,
  IoFolderOutline,
  IoChevronForward,
} from 'react-icons/io5'
import {
  useCardsSavedFixtures,
  useCardsWatchlists,
  createCardsWatchlist,
  deleteCardsWatchlist,
  unsaveCardsFixture,
} from '../../swr-config'
import type { SavedFixtureOut, WatchlistOut } from '../../types'
import { ErrorState } from '../ErrorState'
import { fmtKickoff } from '../../utils'
import toast from 'react-hot-toast'

interface CardsSavedFixturesProps {
  onBack: () => void
  onFixtureSelect: (fixtureId: number) => void
}

type ViewMode = 'all' | 'watchlist'

export function CardsSavedFixtures({ onBack, onFixtureSelect }: CardsSavedFixturesProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('all')
  const [selectedWatchlistId, setSelectedWatchlistId] = useState<number | null>(null)
  const [showCreateWatchlist, setShowCreateWatchlist] = useState(false)
  const [newWatchlistName, setNewWatchlistName] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  const {
    data: savedData,
    error: savedError,
    isLoading: savedLoading,
    mutate: mutateSaved,
  } = useCardsSavedFixtures(selectedWatchlistId)

  const {
    data: watchlistsData,
    error: watchlistsError,
    isLoading: watchlistsLoading,
    mutate: mutateWatchlists,
  } = useCardsWatchlists()

  const handleCreateWatchlist = async () => {
    if (!newWatchlistName.trim() || isCreating) return

    setIsCreating(true)
    try {
      await createCardsWatchlist(newWatchlistName.trim())
      toast.success('Watchlist created')
      setNewWatchlistName('')
      setShowCreateWatchlist(false)
      mutateWatchlists()
    } catch (err: any) {
      toast.error(err.message || 'Failed to create watchlist')
    } finally {
      setIsCreating(false)
    }
  }

  const handleDeleteWatchlist = async (watchlistId: number) => {
    if (!confirm('Delete this watchlist? Saved fixtures will be kept.')) return

    try {
      await deleteCardsWatchlist(watchlistId)
      toast.success('Watchlist deleted')
      if (selectedWatchlistId === watchlistId) {
        setSelectedWatchlistId(null)
        setViewMode('all')
      }
      mutateWatchlists()
      mutateSaved()
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete watchlist')
    }
  }

  const handleUnsaveFixture = async (fixtureId: number, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!confirm('Remove this fixture from saved?')) return

    try {
      await unsaveCardsFixture(fixtureId)
      toast.success('Fixture removed')
      mutateSaved()
    } catch (err: any) {
      toast.error(err.message || 'Failed to remove fixture')
    }
  }

  const handleSelectWatchlist = (watchlist: WatchlistOut) => {
    setSelectedWatchlistId(watchlist.id)
    setViewMode('watchlist')
  }

  const handleBackToAll = () => {
    setSelectedWatchlistId(null)
    setViewMode('all')
  }

  if (savedLoading || watchlistsLoading) {
    return (
      <div className="cards-saved">
        <div className="cards-saved-header">
          <button type="button" className="cards-back-btn" onClick={onBack}>
            <IoArrowBack size={20} />
            <span>Back</span>
          </button>
          <h1 className="cards-saved-title">Saved Fixtures</h1>
        </div>
        <div className="cards-loading">
          <div className="loading-spinner" />
          <p>Loading saved fixtures...</p>
        </div>
      </div>
    )
  }

  if (savedError || watchlistsError) {
    return (
      <div className="cards-saved">
        <div className="cards-saved-header">
          <button type="button" className="cards-back-btn" onClick={onBack}>
            <IoArrowBack size={20} />
            <span>Back</span>
          </button>
          <h1 className="cards-saved-title">Saved Fixtures</h1>
        </div>
        <ErrorState
          title="Failed to load"
          message={savedError?.message || watchlistsError?.message || 'Unknown error'}
          onRetry={() => {
            mutateSaved()
            mutateWatchlists()
          }}
        />
      </div>
    )
  }

  return (
    <div className="cards-saved">
      {/* Header */}
      <div className="cards-saved-header">
        <button type="button" className="cards-back-btn" onClick={viewMode === 'watchlist' ? handleBackToAll : onBack}>
          <IoArrowBack size={20} />
          <span>{viewMode === 'watchlist' ? 'All Saved' : 'Back'}</span>
        </button>
        <h1 className="cards-saved-title">
          {viewMode === 'watchlist' && selectedWatchlistId
            ? watchlistsData.watchlists.find((w) => w.id === selectedWatchlistId)?.name || 'Watchlist'
            : 'Saved Fixtures'}
        </h1>
      </div>

      {/* Watchlists Section (only in 'all' view) */}
      {viewMode === 'all' && (
        <div className="cards-watchlists-section">
          <div className="cards-watchlists-header">
            <h2 className="cards-section-title">Watchlists</h2>
            <button
              type="button"
              className="cards-add-watchlist-btn"
              onClick={() => setShowCreateWatchlist(!showCreateWatchlist)}
            >
              <IoAddOutline size={18} />
              <span>New</span>
            </button>
          </div>

          {/* Create Watchlist Form */}
          {showCreateWatchlist && (
            <div className="cards-create-watchlist">
              <input
                type="text"
                className="cards-watchlist-input"
                placeholder="Watchlist name..."
                value={newWatchlistName}
                onChange={(e) => setNewWatchlistName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateWatchlist()}
                autoFocus
              />
              <button
                type="button"
                className="cards-watchlist-create-btn"
                onClick={handleCreateWatchlist}
                disabled={!newWatchlistName.trim() || isCreating}
              >
                {isCreating ? 'Creating...' : 'Create'}
              </button>
            </div>
          )}

          {/* Watchlists List */}
          {watchlistsData.watchlists.length > 0 ? (
            <div className="cards-watchlists-list">
              {watchlistsData.watchlists.map((watchlist) => (
                <div key={watchlist.id} className="cards-watchlist-item">
                  <button
                    type="button"
                    className="cards-watchlist-btn"
                    onClick={() => handleSelectWatchlist(watchlist)}
                  >
                    <IoFolderOutline size={18} />
                    <span className="cards-watchlist-name">{watchlist.name}</span>
                    <span className="cards-watchlist-count">{watchlist.fixture_count}</span>
                    <IoChevronForward size={16} />
                  </button>
                  <button
                    type="button"
                    className="cards-watchlist-delete"
                    onClick={() => handleDeleteWatchlist(watchlist.id)}
                    aria-label="Delete watchlist"
                  >
                    <IoTrashOutline size={16} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="cards-watchlists-empty">No watchlists yet. Create one to organize your saved fixtures.</p>
          )}
        </div>
      )}

      {/* All Saved / Watchlist Fixtures */}
      <div className="cards-saved-fixtures-section">
        {viewMode === 'all' && (
          <h2 className="cards-section-title">
            All Saved ({savedData.total})
          </h2>
        )}

        {savedData.saved_fixtures.length > 0 ? (
          <div className="cards-saved-fixtures-list">
            {savedData.saved_fixtures.map((saved) => (
              <SavedFixtureItem
                key={saved.id}
                saved={saved}
                onSelect={() => saved.fixture && onFixtureSelect(saved.fixture.fixture_id)}
                onRemove={(e) => saved.fixture && handleUnsaveFixture(saved.fixture.fixture_id, e)}
              />
            ))}
          </div>
        ) : (
          <div className="cards-saved-empty">
            <p>No saved fixtures{viewMode === 'watchlist' ? ' in this watchlist' : ''}.</p>
            <button type="button" className="cards-browse-btn" onClick={onBack}>
              Browse Fixtures
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// Saved Fixture Item Component
function SavedFixtureItem({
  saved,
  onSelect,
  onRemove,
}: {
  saved: SavedFixtureOut
  onSelect: () => void
  onRemove: (e: React.MouseEvent) => void
}) {
  if (!saved.fixture) return null

  const { fixture } = saved

  return (
    <div className="cards-saved-fixture-item">
      <button type="button" className="cards-saved-fixture-btn" onClick={onSelect}>
        <div className="cards-saved-fixture-teams">
          <div className="cards-saved-fixture-team">
            {fixture.home_team_logo && (
              <img src={fixture.home_team_logo} alt="" className="cards-saved-team-logo" />
            )}
            <span>{fixture.home_team_name}</span>
          </div>
          <span className="cards-saved-vs">vs</span>
          <div className="cards-saved-fixture-team">
            {fixture.away_team_logo && (
              <img src={fixture.away_team_logo} alt="" className="cards-saved-team-logo" />
            )}
            <span>{fixture.away_team_name}</span>
          </div>
        </div>
        <div className="cards-saved-fixture-meta">
          <span>{fmtKickoff(fixture.kickoff)}</span>
          <span className="cards-saved-fixture-sep">|</span>
          <span>{fixture.competition_name}</span>
        </div>
        {fixture.projected_total_cards != null && (
          <div className="cards-saved-fixture-projection">
            <span className="cards-saved-projection-value">
              {fixture.projected_total_cards.toFixed(1)}
            </span>
            <span className="cards-saved-projection-label">cards</span>
          </div>
        )}
        {saved.notes && (
          <p className="cards-saved-fixture-notes">{saved.notes}</p>
        )}
      </button>
      <button
        type="button"
        className="cards-saved-fixture-remove"
        onClick={onRemove}
        aria-label="Remove from saved"
      >
        <IoTrashOutline size={16} />
      </button>
    </div>
  )
}
